import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';

import MapWithRegions from 'components/Map/MapOptions/MapWithRegions';
import PatrolPath from 'components/PatrolPath';
import styles from './index.module.scss';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { PATROL_MAP_LINE } from '../../../../utils/constants/patrol';
import {
  EVENT_MAP_ICON_TYPE,
  LOCATION_MODE,
} from '../../../../utils/constants/Map';
import ClearDrawingMenu from '../../../Map/MapOptions/ClearMenu';
import { EVIDENCE_PREFIX } from '../../../../utils/constants/task';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { PAGE_URLS } from 'utils/constants';
import PatrolPathWithEvidenceCollection from 'components/PatrolPathWithEvidenceCollection/patrolPathWithEvidenceCollection';

const MapArtist = ({
  label,
  value: markers = [],
  additionalMarkers = [],
  onChange: setMarkers,
  required,
  error,
  additionalPolygon,
  info,
  additionalPolygonMode,
  maxPoints = 100,
  center,
  formProps,
  locationEvidence,
  displayEvidence,
  disableMarkerClick,
  polylineConfig = PATROL_MAP_LINE,
  hidePolylines,
  centerDefault,
  iconTextType,
  singleMarkerIcon = EVENT_MAP_ICON_TYPE.NON_NEWS_EVENT_SINGLE_LOC,
  typeOfIcon, // this is used when we need to show circles with text generally it is a 'CHECKPOINT' or 'WAYPOINT',
  viewName,
  mediaOnChange,
  displaySites,
  patrolEvidenceOptions = false,
  uploadPatrolTask,
  disabled = false,
  evidenceCollection,
  secondaryDetail,
}) => {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const [drawingMode, setDrawingMode] = useState(true);
  const isPatrolInstancePanel = viewName === PAGE_URLS.PATROLS.link;

  useEffect(() => {
    if (formProps.values?.patrolledLocations !== undefined) {
      const { patrolledLocations } = formProps.values;
      const checkedLocations = [];
      markers.forEach(marker => {
        const patrolled = patrolledLocations.find(
          location => location?.definedLocation?.qrCode === marker.qrCode,
        );
        if (
          patrolledLocations &&
          patrolledLocations.length > 0 &&
          marker.isWaypoint
        ) {
          checkedLocations.push(marker);
        }
        if (patrolled) {
          if (patrolled.timestamp) {
            checkedLocations.push({
              ...marker,
              patrolled: true,
              isPatrolDisabled: true,
            });
          } else {
            checkedLocations.push({
              ...marker,
              patrolled: false,
              isPatrolDisabled: false,
            });
          }
        }
      });

      if (checkedLocations.length) {
        if (
          checkedLocations[checkedLocations.length - 1].qrCode ===
          checkedLocations[0].qrCode
        ) {
          const lastCheckpoint = checkedLocations[checkedLocations.length - 1];
          lastCheckpoint.patrolled = false;
          lastCheckpoint.isPatrolDisabled = false;
          checkedLocations[checkedLocations.length - 1] = lastCheckpoint;
        }
        setMarkers(checkedLocations);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formProps.values?.patrolledLocations]);

  const handleChange = (e, { lat, lng }) => {
    let counter = 1;
    const cpArray = [];
    const wpArray = [];

    markers.forEach(marker => {
      if (marker.lat === lat && marker.lng === lng) {
        cpArray.push({
          ...marker,
          type: e.target.checked ? 'CHECKPOINT' : 'WAYPOINT',
          text: e.target.checked ? counter : null,
        });
        if (e.target.checked) {
          counter += 1;
        }
      } else if (marker.type === 'CHECKPOINT') {
        cpArray.push({ ...marker, text: counter });
        counter += 1;
      } else {
        wpArray.push({ ...marker, text: null });
      }
    });

    //  Arrange the sequence incase there is a duplicate array to the following
    // SeqNumber : 1
    //  SeqNumber : 2
    // SeqNumber : 3
    // SeqNumber : 1

    sortCombineAndSetMarkers(cpArray, wpArray);
  };

  const virtualPatrolHandleChange = ({ checked, key }) => {
    const patrolArray = [];
    if (key.seqNumber === 1 && checked && key.type === 'CHECKPOINT') {
      markers.forEach((marker, index) => {
        if (index === 0) {
          patrolArray.push({
            ...marker,
            isPatrolDisabled: true,
            patrolled: checked,
          });
        } else {
          patrolArray.push({
            ...marker,
            isPatrolDisabled: false,
            patrolled: false,
          });
        }
      });

      setMarkers(patrolArray);
      uploadPatrolTask(patrolArray);
    } else if (checked && key.type === 'CHECKPOINT') {
      markers.forEach(marker => {
        if (marker.qrCode === key.qrCode) {
          patrolArray.push({
            ...marker,
            patrolled: checked,
            isPatrolDisabled: true,
          });
        } else {
          patrolArray.push({
            ...marker,
          });
        }
      });
      setMarkers(patrolArray);
      uploadPatrolTask(patrolArray);
    }
  };

  const handleMarkerToggle = ({ evt }) => {
    let counter = 1;
    const cpArray = [];
    const wpArray = [];
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();

    markers.forEach(marker => {
      // Condition 1: (click on the marker)

      // Condition 2: (did not click on marker)

      if (marker.lat === lat && marker.lng === lng) {
        if (marker.text === 1) {
          cpArray.push(marker);
          counter += 1;
          setDrawingMode(true);
        } else {
          cpArray.push({
            ...marker,
            type: marker.type === 'WAYPOINT' ? 'CHECKPOINT' : 'WAYPOINT',
            text: marker.type === 'WAYPOINT' ? counter : null,
          });
          if (marker.type === 'WAYPOINT') {
            counter += 1;
          }
        }
      } else if (marker.type === 'CHECKPOINT') {
        cpArray.push({ ...marker, text: counter });
        counter += 1;
      } else {
        wpArray.push({ ...marker, text: null });
      }
    });

    sortCombineAndSetMarkers(cpArray, wpArray);
  };

  const iconOrCircleWithText = (lat, lng) => {
    if (singleMarkerIcon) {
      // first point is an icon
      return {
        id: v4(),
        seqNumber: 0,
        lat,
        lng,
        iconTextType,
        icon: singleMarkerIcon,
      };
    } else {
      // a circle with text
      return {
        id: v4(),
        seqNumber: 0,
        lat,
        lng,
        type: typeOfIcon,
        text: 1,
      };
    }
  };

  const onMapClick = evt => {
    if (isPatrolInstancePanel) {
      return;
    }
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const noOfPoints = markers.length;
    if (maxPoints === 1) {
      setMarkers(
        markers.length < 1
          ? [iconOrCircleWithText(lat, lng)]
          : markers.map(item => {
              return item.icon
                ? {
                    seqNumber: 0,
                    lat,
                    lng,
                    id: item.id,
                    icon: singleMarkerIcon,
                  }
                : {
                    ...item,
                    ...iconOrCircleWithText(lat, lng),
                    id: item.id || v4(),
                    markerDraggable: false,
                  };
            }),
      );
      return;
    }

    if (noOfPoints < maxPoints) {
      if (
        markers.length > 1 &&
        markers[0].seqNumber === markers[markers.length - 1].seqNumber
      ) {
        // last point is a duplicate, so actual number is total - 1
        const actualPoints = noOfPoints - 1;
        const prevMarkers = cloneDeep(markers);
        const lastMarker = prevMarkers[prevMarkers.length - 1];
        // delete Last
        prevMarkers.pop();
        // add the new
        prevMarkers.push({
          seqNumber: actualPoints + 1,
          disabled: markers.length === 0,
          lat,
          lng,
          type: markers.length === 0 ? 'CHECKPOINT' : 'WAYPOINT',
          text: markers.length === 0 ? 1 : null,
        });
        // copy the last marker
        prevMarkers.push(lastMarker);
        setMarkers(prevMarkers);
      } else {
        setMarkers(
          markers.concat({
            seqNumber: noOfPoints + 1,
            disabled: markers.length === 0,
            lat,
            lng,
            type: markers.length === 0 ? 'CHECKPOINT' : 'WAYPOINT',
            text: markers.length === 0 ? 1 : null,
          }),
        );
      }
    }
  };

  const markerDragStart = evt => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const dragged = markers.find((marker, index) => {
      if (marker.lat === lat && marker.lng === lng) {
        return { ...marker, index };
      }
      return undefined;
    });
    dragRef.current = { ...dragged };
  };

  const markerDragEnd = evt => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    const markersClone = markers;
    if (
      markersClone.length > 2 &&
      markersClone[0].lat === markersClone[markersClone.length - 1].lat &&
      markersClone[0].lng === markersClone[markersClone.length - 1].lng &&
      dragRef.current.seqNumber === 1
    ) {
      markersClone[markersClone.length - 1] = {
        ...dragRef.current,
        lat,
        lng,
      };
    }

    markersClone[dragRef.current.seqNumber - 1] = {
      ...dragRef.current,
      lat,
      lng,
    };
    setMarkers([...markersClone]);
  };

  const reset = () => {
    setMarkers([]);
    setDrawingMode(true);
  };

  const onEvidenceChange = ({ value: evidence, seqNumber, point }) => {
    if (isPatrolInstancePanel) {
      const updatedCheckpointDetails = formProps.values.checkPointDetail;
      const updatedCheckpoints = formProps.values?.checkpoints;
      const checkpointReplaceIndex = updatedCheckpointDetails.findIndex(
        checkpoint => checkpoint.qrCode === point.qrCode,
      );
      const normalCheckpointReplaceIndex = updatedCheckpoints.findIndex(
        checkpoint => checkpoint.qrCode === point.qrCode,
      );
      Object.assign(updatedCheckpointDetails[checkpointReplaceIndex], point);
      updatedCheckpoints[normalCheckpointReplaceIndex].taskId = point.taskId;
      formProps.setFieldValue('checkPointDetail', updatedCheckpointDetails);
      formProps.setFieldValue('checkpoints', updatedCheckpoints);
    }

    const key = `${EVIDENCE_PREFIX}${seqNumber}`;
    const val = locationEvidence || { [key]: null };

    /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
    const { [key]: updatedEvidence, ...rest } = val;

    const newPatrolPath = {
      ...rest,
      [key]: { ...point, value: evidence },
    };
    formProps.setFieldValue('locationEvidence', newPatrolPath);
  };

  const sortCombineAndSetMarkers = (cpArray, wpArray) => {
    if (cpArray.length === 1 && wpArray.length < 1) return;
    const sortedCombine = [...cpArray, ...wpArray].sort(
      (a, b) => a.seqNumber - b.seqNumber,
    );
    //Sequence after sorting
    // SeqNumber : 1
    // SeqNumber : 1
    // SeqNumber : 2
    //SeqNumber : 3

    if (sortedCombine[0].seqNumber === sortedCombine[1].seqNumber) {
      const [first] = sortedCombine;
      sortedCombine.splice(0, 2); // remove duplicates
      sortedCombine.unshift(first); // push first to first
      sortedCombine.push(first); // push first to last
      setMarkers(sortedCombine);
    } else {
      setMarkers(sortedCombine);
    }
  };

  const disableLastMarker = markers => {
    if (
      markers.length > 1 &&
      markers[0].lat === markers[markers.length - 1].lat &&
      markers[0].lng === markers[markers.length - 1].lng &&
      !isPatrolInstancePanel
    ) {
      const withNoEvidence = cloneDeep(markers[0]);
      // adding this key removes evidence box
      withNoEvidence['noEmbeddedEvidenceForm'] = true;
      //removes the last index of the array
      markers.pop();
      //places a copy of the first index in the place where the last index was
      markers.push(withNoEvidence);
      //returns the array
      return markers;
    } else {
      return markers;
    }
  };

  const textErrorOrBool = typeof error === 'string' ? t(error) : error;

  return (
    <div className={styles.panelContainer}>
      <InputLabel
        shrink
        required={required}
        error={textErrorOrBool}
        style={{ display: 'flex' }}
      >
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <span className={styles.error}>{textErrorOrBool}</span>

      <div className={styles.mapPanel}>
        <MapWithRegions
          mapInForm={true}
          requirePan={true}
          markers={markers.concat(additionalMarkers)}
          center={center || centerDefault}
          secondaryPolygon={additionalPolygon}
          secondaryDetail={secondaryDetail} // this contains info for centre of gravity for panning to a location
          secondaryPolygonMode={additionalPolygonMode}
          markerDraggable={true}
          {...(!isPatrolInstancePanel && { markerDragStart: markerDragStart })}
          {...(!isPatrolInstancePanel && { markerDragEnd: markerDragEnd })}
          onMapClick={drawingMode && !disabled ? onMapClick : null}
          isZoomEnabled={false}
          {...(!disableMarkerClick && {
            markerClick: handleMarkerToggle,
          })}
          {...(!hidePolylines && {
            polylines: {
              markers:
                markers.concat(additionalMarkers).length > 1
                  ? markers.concat(additionalMarkers)
                  : [],
              ...polylineConfig,
            },
          })}
          {...(markers.length > 0 &&
            !isPatrolInstancePanel &&
            !disabled && {
              customMapControl: <ClearDrawingMenu onClick={reset} />,
            })}
          displaySites={displaySites}
        />
      </div>
      <div>
        {maxPoints > 1 && markers.length > 0 && !evidenceCollection && (
          <PatrolPath
            value={disableLastMarker(markers)}
            handleChange={handleChange}
            onEvidenceChange={onEvidenceChange}
            locationEvidence={locationEvidence}
            displayEvidence={displayEvidence}
            virtualPatrolHandleChange={virtualPatrolHandleChange}
            checkpointDetails={formProps.values?.checkPointDetail}
            viewName={viewName}
            formProps={formProps}
            mediaOnChange={mediaOnChange}
            patrolEvidenceOptions={patrolEvidenceOptions}
            isPatrolInstancePanel={isPatrolInstancePanel}
          />
        )}
        {evidenceCollection && (
          <PatrolPathWithEvidenceCollection
            checkpointDetails={formProps.values?.checkPointDetail}
            uploadPatrolTask={uploadPatrolTask}
          />
        )}
      </div>
    </div>
  );
};

MapArtist.propTypes = {
  label: PropTypes.string,
  value: PropTypes.array,
  onChange: PropTypes.func,
  uploadPatrolTask: PropTypes.func,
  required: PropTypes.bool,
  patrolEvidenceOptions: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  additionalPolygon: PropTypes.array,
  info: InfoPropTypes,
  additionalPolygonMode: PropTypes.oneOf(Object.values(LOCATION_MODE)),
  maxPoints: PropTypes.number,
  center: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number }),
  singleMarkerIcon: PropTypes.string,
  formProps: PropTypes.object,
  locationEvidence: PropTypes.object,
  displayEvidence: PropTypes.bool,
  additionalMarkers: PropTypes.array,
  disableMarkerClick: PropTypes.bool,
  polylineConfig: PropTypes.object,
  hidePolylines: PropTypes.bool,
  displaySites: PropTypes.bool,
  evidenceCollection: PropTypes.bool,
  disabled: PropTypes.bool,
  centerDefault: PropTypes.object,
  iconTextType: PropTypes.string,
  typeOfIcon: PropTypes.string,
  viewName: PropTypes.string,
  mediaOnChange: PropTypes.func,
  checkpointDetails: PropTypes.array,
  secondaryDetail: PropTypes.object,
};

MapArtist.defaultProps = {
  value: [],
  evidenceCollection: false,
};

export default MapArtist;
